// Auth service for session management and redirect logic

const APP_URL = 'https://app.nisa.coach'

interface BetterAuthSession {
  session: {
    id: string
    userId: string
    expiresAt: string
    [key: string]: unknown
  } | null
  user: unknown | null
}

/**
 * Check if the visitor has an active session on the main app.
 * Uses Better Auth's standard /api/auth/get-session endpoint.
 *
 * This only ever reads whether a session exists. No session contents, user
 * fields, or identifiers are stored or logged on the marketing site.
 */
export const checkSession = async (): Promise<{ authenticated: boolean }> => {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), 3000)

  try {
    const response = await fetch(`${APP_URL}/api/auth/get-session`, {
      method: 'GET',
      credentials: 'include', // Cross-subdomain session cookie
      headers: {
        'Content-Type': 'application/json',
      },
      signal: controller.signal,
    })

    clearTimeout(timeoutId)

    if (!response.ok) {
      // The body may be empty or malformed on 502/503, so don't read it.
      return { authenticated: false }
    }

    let data: BetterAuthSession
    try {
      data = await response.json()
    } catch {
      return { authenticated: false }
    }

    // Better Auth returns { session, user } when authenticated,
    // { session: null, user: null } when not.
    return { authenticated: !!data.session && !!data.user }
  } catch {
    clearTimeout(timeoutId)

    // Fail open: a timeout or network error must not block the marketing site.
    return { authenticated: false }
  }
}

/**
 * Redirect to the main app dashboard.
 */
export const redirectToApp = (): void => {
  window.location.href = APP_URL
}
